import React, { useState } from "react";
import axios from "axios";
import { useLanguage } from "../../contexts/LanguageContext";
import { usePageContent } from "../../hooks/usePageContent";
import { SimplePageLayout } from "../../components/layout";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const OurStoryPage = () => {
  const { language } = useLanguage();
  const { content, loading } = usePageContent("our-story");
  
  if (loading) return <SimplePageLayout><div className="text-center py-12">Chargement...</div></SimplePageLayout>;
  
  const c = content || {};
  const values = (language === 'fr' ? c.values_fr : c.values_en || "").split("\n").filter(v => v).map(v => {
    const [title, desc] = v.split("|");
    return { title, desc };
  });
  
  return (
    <SimplePageLayout>
      <div className="max-w-3xl mx-auto">
        <span className="calli-badge mb-4">💡 {language === 'fr' ? 'Notre histoire' : 'Our story'}</span>
        <h1 className="text-4xl md:text-5xl font-black text-gray-900 mb-6">
          {language === 'fr' ? c.title_fr : c.title_en}
        </h1>
        
        <div className="prose prose-lg text-gray-600 space-y-6">
          <p className="text-xl leading-relaxed">
            {language === 'fr' ? c.intro_fr : c.intro_en}
          </p>
          
          <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {language === 'fr' ? c.mission_title_fr : c.mission_title_en}
            </h2>
            <p>
              {language === 'fr' ? c.mission_text_fr : c.mission_text_en}
            </p>
          </div>
          
          {values.length > 0 && (
            <div className="bg-orange-50 rounded-2xl p-8 border border-orange-100">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                {language === 'fr' ? 'Nos valeurs' : 'Our values'}
              </h2>
              <ul className="space-y-3">
                {values.map((value, i) => (
                  <li key={i}>
                    <strong className="text-orange-600">{value.title}</strong>: {value.desc}
                  </li>
                ))}
              </ul>
            </div>
          )}
          
          {c.image_url && (
            <img src={c.image_url} alt="" className="w-full rounded-2xl" />
          )}
        </div>
      </div>
    </SimplePageLayout>
  );
};

export const TeamPage = () => {
  const { language } = useLanguage();
  const { content, loading } = usePageContent("team");
  
  if (loading) return <SimplePageLayout><div className="text-center py-12">Chargement...</div></SimplePageLayout>;
  
  const c = content || {};
  
  return (
    <SimplePageLayout>
      <div className="max-w-3xl mx-auto">
        <span className="calli-badge mb-4">👥 {language === 'fr' ? 'Équipe' : 'Team'}</span>
        <h1 className="text-4xl md:text-5xl font-black text-gray-900 mb-6">
          {language === 'fr' ? c.title_fr : c.title_en}
        </h1>
        
        <p className="text-xl text-gray-600 mb-8">
          {language === 'fr' ? c.intro_fr : c.intro_en}
        </p>
        
        <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
          <p className="text-gray-600 text-center">
            {language === 'fr' 
              ? "📸 Photos et biographies de l'équipe à venir..."
              : "📸 Team photos and bios coming soon..."}
          </p>
        </div>
      </div>
    </SimplePageLayout>
  );
};

export const PressPage = () => {
  const { language } = useLanguage();
  const { content, loading } = usePageContent("press");
  
  if (loading) return <SimplePageLayout><div className="text-center py-12">Chargement...</div></SimplePageLayout>;
  
  const c = content || {};
  
  return (
    <SimplePageLayout>
      <div className="max-w-3xl mx-auto">
        <span className="calli-badge mb-4">📰 {language === 'fr' ? 'Presse' : 'Press'}</span>
        <h1 className="text-4xl md:text-5xl font-black text-gray-900 mb-6">
          {language === 'fr' ? c.title_fr : c.title_en}
        </h1>
        
        <div className="space-y-6">
          <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {language === 'fr' ? 'Kit presse' : 'Press kit'}
            </h2>
            <p className="text-gray-600 mb-4">
              {language === 'fr' ? c.kit_text_fr : c.kit_text_en}
            </p>
            {c.kit_url && (
              <a href={c.kit_url} target="_blank" rel="noopener noreferrer" className="calli-btn-primary inline-block">
                {language === 'fr' ? 'Télécharger le kit presse' : 'Download press kit'}
              </a>
            )}
          </div>
          
          <div className="bg-orange-50 rounded-2xl p-8 border border-orange-100">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {language === 'fr' ? 'Contact presse' : 'Press contact'}
            </h2>
            <p className="text-gray-600">
              {language === 'fr' 
                ? "Pour toute demande presse, contactez-nous à :"
                : "For press inquiries, contact us at:"}
            </p>
            <p className="font-bold text-orange-600 mt-2">{c.contact_email}</p>
          </div>
        </div>
      </div>
    </SimplePageLayout>
  );
};

export const ContactPage = () => {
  const { language } = useLanguage();
  const { content, loading } = usePageContent("contact");
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/contact`, formData);
      alert(language === 'fr' ? 'Message envoyé ! Nous vous répondrons rapidement.' : "Message sent! We'll reply soon.");
      setFormData({ name: '', email: '', message: '' });
    } catch (err) {
      alert(language === 'fr' ? "Erreur lors de l'envoi" : 'Error sending message');
    }
  };
  
  if (loading) return <SimplePageLayout><div className="text-center py-12">Chargement...</div></SimplePageLayout>;
  
  return (
    <SimplePageLayout>
      <div className="max-w-3xl mx-auto">
        <span className="calli-badge mb-4">✉️ {language === 'fr' ? 'Contact' : 'Contact'}</span>
        <h1 className="text-4xl md:text-5xl font-black text-gray-900 mb-6">
          {language === 'fr' ? 'Nous contacter' : 'Contact us'}
        </h1>
        
        <div className="grid md:grid-cols-2 gap-8">
          <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              {language === 'fr' ? 'Envoyez-nous un message' : 'Send us a message'}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <input
                type="text"
                placeholder={language === 'fr' ? 'Votre nom' : 'Your name'}
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:border-orange-500"
                required
              />
              <input
                type="email"
                placeholder={language === 'fr' ? 'Votre email' : 'Your email'}
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:border-orange-500"
                required
              />
              <textarea
                placeholder={language === 'fr' ? 'Votre message' : 'Your message'}
                value={formData.message}
                onChange={(e) => setFormData({...formData, message: e.target.value})}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:border-orange-500 h-32"
                required
              />
              <button type="submit" className="calli-btn-primary w-full">
                {language === 'fr' ? 'Envoyer' : 'Send'}
              </button>
            </form>
          </div>
          
          <div className="space-y-4">
            {/* CEO Direct Line - Trust builder */}
            <div className="bg-gradient-to-br from-orange-100 to-yellow-50 rounded-2xl p-6 border-2 border-orange-200 relative overflow-hidden">
              <div className="absolute top-0 right-0 bg-orange-500 text-white text-xs font-bold px-3 py-1 rounded-bl-lg">
                {language === 'fr' ? 'DIRECT' : 'DIRECT'}
              </div>
              <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                📱 {language === 'fr' ? 'Le fondateur vous répond' : 'The founder answers you'}
              </h3>
              <p className="text-gray-600 text-sm mb-3">
                {language === 'fr' 
                  ? "Aux prémices du projet, Raphaël répond personnellement à toutes vos questions."
                  : "In the early days of the project, Raphaël personally answers all your questions."}
              </p>
              <a 
                href="tel:+33642253607" 
                className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-bold px-4 py-2 rounded-xl transition-colors"
              >
                <span>+33 6 42 25 36 07</span>
              </a>
              <p className="text-xs text-gray-500 mt-2">
                {language === 'fr' ? 'WhatsApp / SMS / Appel' : 'WhatsApp / SMS / Call'}
              </p>
            </div>

            <div className="bg-orange-50 rounded-2xl p-6 border border-orange-100">
              <h3 className="font-bold text-gray-900 mb-2">📧 Email</h3>
              <p className="text-orange-600">contact@calli.fr</p>
            </div>
            <div className="bg-orange-50 rounded-2xl p-6 border border-orange-100">
              <h3 className="font-bold text-gray-900 mb-2">📍 {language === 'fr' ? 'Adresse' : 'Address'}</h3>
              <p className="text-gray-600">Paris, France</p>
            </div>
          </div>
        </div>
      </div>
    </SimplePageLayout>
  );
};
